import Main from "niteroi/packages/air-passenger-rights/controllers/Main.controller";
import Table from "sap/m/Table";
import ListBinding from "sap/ui/model/ListBinding";
import {Button$PressEvent} from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import Page from "sap/m/Page";
import Ui5File from "sap/ui/core/util/File";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {ListItemBase$PressEvent} from "sap/m/ListItemBase";
import {Select$ChangeEvent} from "sap/m/Select";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

export default class Airports extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);

            if (route.getPattern().includes("airports/add")) {
                this.resetOriginalModel("airport");
            } else {
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    public onBeforeRendering(): void {
        this.setActiveTileByRoute();
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        this.resetOriginalModel("airport");
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        const url = objectId ? `/api/air-passenger-rights/airports/${objectId}` : "/api/air-passenger-rights/airports";
        const modelName = objectId ? "airport" : "airports";
        this.fetchData(url, modelName, objectId);
    }

    /**
     * Handle continent selection change
     * Updates the continent name based on the selected code
     * @param event - Selection change event
     */
    public onChangeContinent(event: Select$ChangeEvent): void {
        const source = event.getSource();
        const selectedItem = source.getSelectedItem();
         if (selectedItem) {
            const continentName = selectedItem.getText();
            const model = this.getModel("airport");
            if (model) {
                model.setProperty("/continent", continentName);
            }
        }
    }

    /**
     * Handle airport press in the table
     * Loads the selected airport data into the form
     * @param event - Press event
     */
    public onPressColumnListItemDetail(event: ListItemBase$PressEvent): void {
        this.resetOriginalModel("airport");
        const source = event.getSource();
        const bindingContext = source.getBindingContext("airports");
        const selectedListItem = bindingContext.getModel().getProperty(bindingContext.getPath());
        this.navTo("air-passenger-rights/airports/edit", {id: selectedListItem.airportId}, false);
    }

    /**
     * Search handler for airlines table
     * Filters airlines by IATA code, ICAO code, name, country, callsign, and alias
     * @param event - The search event containing the search query
     */
    public onListSearch(event: SearchField$LiveChangeEvent): void {
        const searchQuery = event.getParameter("newValue").trim();
        const airportsTable = this.byId("idAirportsTable") as Table;

        if (!airportsTable) {
            return;
        }

        const binding = airportsTable.getBinding("items") as ListBinding;
        this.bindFilters(binding, searchQuery);
    }

    public onPressMenuItemAdd(): void {
        this.resetOriginalModel("airport");
        this.navTo("air-passenger-rights/airports/add", {}, false);
    }

    public onPressButtonSaveFormEdit(event: Button$PressEvent): void {
        const source = event.getSource();
        const page = ((source.getParent() as Toolbar).getParent() as SimpleForm).getParent() as Page;
        page.setBusy(true);
        const model = this.getModel("airport");
        if (JSON.stringify(this.originalData) !== model.getJSON()) {
            const airportId = model.getProperty("/airportId") as string;
            const method = airportId ? "PUT" : "POST";
            const requestModel = this.createRequestModel();
            const url = airportId ? `/api/air-passenger-rights/airports/${airportId}` : "/api/air-passenger-rights/airports";
            const headers = this.getRequestHeaders();
            requestModel.loadData(url, model.getJSON(), true, method, false, true, headers).then(() => {
                model.setData(JSON.parse(requestModel.getJSON()));
                this.showToast(this.getText("msgAirportSavedSuccess"));
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(`${responseText.code} - ${responseText.error}`);
            }).finally(() => {
                page.setBusy(false);
            });
        } else {
            page.setBusy(false);
        }
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("airport");
    }

    public onPressMenuItemExportJson(): void {
        this.exportToJSON("airports");
    }

    public onPressDownloadTemplateBtn(): void {
        // Template based on flattened Airport structure
        const template = [{
            "iataCode": "LHR",
            "icaoCode": "EGLL",
            "name": "Heathrow Airport",
            "type": "International",
            "timezone": "Europe/London",
            "elevation": 83,
            "address": "Longford",
            "number": "",
            "postalCode": "TW6",
            "city": "London",
            "state": "England",
            "stateCode": "ENG",
            "country": "United Kingdom",
            "countryCode": "GB",
            "continent": "Europe",
            "continentCode": "EU",
            "latitude": 51.470020,
            "longitude": -0.454295,
            "phone": "+44 844 335 1801",
            "website": "https://www.heathrow.com",
            "email": "feedback@heathrow.com",
            "keywords": "",
            "isActive": true
        }];
        Ui5File.save(JSON.stringify(template, null, 4), "airports_template", "json", "application/json", "utf-8");
    }
}
